// Frozen contract for Badge Builder / unit HTML:
// <flash-cards config='{"cards":[{"front":{"html":"..."},"back":{"html":"..."},"isTrueFalse":false}]}'></flash-cards>
// Legacy <thc-flash-cards> uses the same config shape (dual-hydrate).
//
// Returns final props for FlashcardsHost (items with FlashcardsFace nodes),
// or null so the placeholder can stay in the page.

import React from 'react';
import {FlashcardsFace} from '@trailhead-emu/design-system';
import {t} from 'lib/I18n';
import {parseJsonConfig} from 'lib/htmlIsland/parseJsonConfig';
import {sanitizeHtml} from 'lib/htmlIsland/sanitizeHtml';
import {htmlNode} from 'lib/htmlIsland/htmlNode';

const LABEL_SCOPE = 'views.trailhead.unit.flash_cards';

// Both faces sit inside TDS2's flip control (role="button"). Only non-interactive
// phrasing content is allowed so we do not nest links or buttons (WCAG 2.2).
const FACE_PURIFY = {
  ALLOWED_TAGS: [
    'abbr',
    'b',
    'bdi',
    'bdo',
    'br',
    'cite',
    'code',
    'data',
    'del',
    'dfn',
    'em',
    'i',
    'img',
    'ins',
    'kbd',
    'mark',
    'q',
    's',
    'samp',
    'small',
    'span',
    'strong',
    'sub',
    'sup',
    'time',
    'u',
    'var',
    'wbr'
  ],
  ALLOWED_ATTR: ['alt', 'src'],
  KEEP_CONTENT: true
};

export function parseConfig(element) {
  const parsed = parseJsonConfig(element, {warnLabel: 'flash-cards'});
  if (!parsed || !Array.isArray(parsed.cards)) {
    return null;
  }

  const labels = faceLabels();
  const items = parsed.cards.map((card, index) => toItem(card, index, labels)).filter(Boolean);
  if (items.length === 0) {
    return null;
  }

  return {items};
}

function faceLabels() {
  return {
    question: t(`${LABEL_SCOPE}.question_label`),
    answer: t(`${LABEL_SCOPE}.answer_label`),
    trueOrFalse: t(`${LABEL_SCOPE}.true_or_false_label`),
    trueLabel: t(`${LABEL_SCOPE}.true_label`),
    falseLabel: t(`${LABEL_SCOPE}.false_label`)
  };
}

function toItem(card, index, labels) {
  if (card == null || typeof card !== 'object') {
    return null;
  }

  // Informative image without alt — drop the card; never emit <img alt="">.
  if (imageMissingAlt(card.front) || imageMissingAlt(card.back)) {
    return null;
  }

  const front = toFrontFace(card.front, card, labels);
  const back = toBackFace(card.back, card, labels);
  if (!front && !back) {
    return null;
  }

  return {
    id: String(index),
    front: front || emptyFace(labels.question, 'emphasis'),
    back: back || emptyFace(labels.answer, 'body')
  };
}

function imageMissingAlt(side) {
  if (side == null || typeof side !== 'object') {
    return false;
  }
  if (!side.image || typeof side.image !== 'object' || !side.image.src) {
    return false;
  }
  return !String(side.image.alt ?? '').trim();
}

function toFrontFace(front, card, labels) {
  if (front == null || typeof front !== 'object') {
    return null;
  }

  const isTrueFalse = Boolean(card.isTrueFalse);
  if (front.image && typeof front.image === 'object' && front.image.src) {
    // Informative card images need real alt; empty alt marks the image decorative.
    const src = String(front.image.src);
    const alt = String(front.image.alt ?? '').trim();
    if (!alt) {
      return null;
    }
    return React.createElement(
      FlashcardsFace,
      {
        label: isTrueFalse ? labels.trueOrFalse : undefined,
        variant: 'media'
      },
      React.createElement('img', {src, alt})
    );
  }

  const html = String(front.html ?? '');
  if (!html && !isTrueFalse) {
    return null;
  }

  return React.createElement(
    FlashcardsFace,
    {
      label: isTrueFalse ? labels.trueOrFalse : labels.question,
      variant: 'emphasis'
    },
    htmlNode(sanitizeHtml(html, FACE_PURIFY))
  );
}

function toBackFace(back, card, labels) {
  if (back == null || typeof back !== 'object') {
    return null;
  }

  const html = String(back.html ?? '');
  const isTrueFalse = Boolean(card.isTrueFalse);
  if (!html && !isTrueFalse) {
    return null;
  }

  const label = isTrueFalse
    ? (card.trueFalseAnswer ? labels.trueLabel : labels.falseLabel)
    : labels.answer;

  return React.createElement(
    FlashcardsFace,
    {label, variant: 'body'},
    htmlNode(sanitizeHtml(html, FACE_PURIFY))
  );
}

function emptyFace(label, variant) {
  return React.createElement(FlashcardsFace, {label, variant}, null);
}
